import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getThankYou } from "../../reducers/thankYouSlice";
import {
  StyledBanner,
  StyledBannerText,
  StyledLink,
  StyledNewButton,
  StyledNewIcon,
  StyledList,
  StyledNewListItem,
  StyledSubtitle,
  StyledThankYou,
  StyledTitle,
} from "./ThankYou.styled";
import {
  FINAL_DETAILS_PATH,
  THANK_YOU_PATH,
  HOME_PATH,
} from "../../constants/paths";
import { resetForm } from "../../reducers/formSlice";
import { resetLanguageState } from "../../reducers/languageOptionsSlice";
import {
  initializeDigitalData,
  updateInitializedDigitalDataForFirstPage,
} from "../../hooks/scriptsHooks";

const ThankYou = () => {
  const { page_content, loading, error } = useSelector(
    (state) => state.thankYouScreen
  );

  const { sf_lead_id } = useSelector((state) => state.salesForceLeadId);

  const { country_name: countrySelected } = useSelector((state) => state.form);

  const navigate = useNavigate();

  if (!countrySelected) {
    navigate(HOME_PATH, { replace: true });
  }
  const { value: languageCode, country_iso3 } = useSelector(
    (state) => state.languageOptions
  );

  const dispatch = useDispatch();

  React.useEffect(() => {
    dispatch(getThankYou(countrySelected, languageCode, !!error?.message));
  }, [dispatch, countrySelected, languageCode, error?.message]);

  React.useEffect(() => {
    if (window.digitalData) {
      updateInitializedDigitalDataForFirstPage();
      window.digitalData.push({
        event: {
          name: "pageview",
        },
        journey: {
          name: "new-registration",
          stepNumber: "6",
          stepName: "thank you for registering",
          id: sf_lead_id,
        },
        pageInfo: {
          pageTitle: "thank you for registering with bca",
          pagePath: `/${THANK_YOU_PATH}`,
          previousPagePath: `/${FINAL_DETAILS_PATH}`,
        },
      });
    } else {
      initializeDigitalData();
      updateInitializedDigitalDataForFirstPage();
      window.digitalData.push({
        event: {
          name: "pageview",
        },
        journey: {
          name: "new-registration",
          stepNumber: "6",
          stepName: "thank you for registering",
          id: sf_lead_id,
        },
        pageInfo: {
          pageTitle: "thank you for registering with bca",
          pagePath: `/${THANK_YOU_PATH}`,
          previousPagePath: `/${FINAL_DETAILS_PATH}`,
        },
        site: {
          brandCode: country_iso3,
          brandName: "bca europe",
          language: languageCode,
          platformType: "cms",
          country: languageCode,
        },
      });
    }
  }, [country_iso3, languageCode, sf_lead_id]);

  return (
    <StyledThankYou>
      {!loading && (
        <>
          <StyledNewIcon name="success" size="xxl" />
          <StyledTitle>{page_content?.header}</StyledTitle>
          <StyledSubtitle>{page_content?.body}</StyledSubtitle>
          {!!error?.message && (
            <StyledBanner
              variant="warning"
              displayMode="inline"
              title={page_content?.file_infected_title}
            >
              <StyledBannerText>
                {page_content?.file_infected_warning_message_list?.message}
                <StyledList>
                  {page_content?.file_infected_warning_message_list?.list_of_text_fields.map(
                    (listItem) => (
                      <StyledNewListItem readonly key={listItem?.item_text}>
                        {listItem?.item_text}
                      </StyledNewListItem>
                    )
                  )}
                </StyledList>
              </StyledBannerText>
            </StyledBanner>
          )}
          <StyledLink href={page_content?.back_to_bca_button?.value}>
            <StyledNewButton
              data-cy="thank-you-back-to-bca-button"
              variant="primary"
              text={page_content?.back_to_bca_button?.label}
              onClick={() => {
                dispatch(resetForm());
                dispatch(resetLanguageState());
              }}
            />
          </StyledLink>
        </>
      )}
    </StyledThankYou>
  );
};

export default ThankYou;
